import { Weekday } from '@vallift/ngl';
import { MapLocationValue } from '@shared/components/map-location-picker/map-location-picker';
import { hydrateGridChoices, type GridChoiceOption } from '../utils/grid-choice-option';
import { NumberGridColumn } from '@shared/components/number-grid/number-grid';

export type FieldType =
  | 'heading'
  | 'paragraph'
  | 'divider'
  | 'spacer'
  | 'text'
  | 'textarea'
  | 'email'
  | 'phone'
  | 'number'
  | 'currency'
  | 'url'
  | 'checkbox'
  | 'checkbox_group'
  | 'radio_group'
  | 'switch'
  | 'select'
  | 'searchable_picker'
  | 'single_choice'
  | 'multi_choice'
  | 'multi_choice_grid'
  | 'date'
  | 'time'
  | 'datetime'
  | 'date_range'
  | 'time_range'
  | 'slider'
  | 'rating'
  | 'range_slider'
  | 'color'
  | 'numeric_stepper'
  | 'image_picker'
  | 'camera'
  | 'live_geo_coordinates'
  | 'address_selector'
  | 'map'
  | 'file_uploader'
  | 'audio_recorder'
  | 'video_recorder'
  | 'rich_text'
  | 'document_scanner'
  | 'drawing'
  | 'signature'
  | 'captcha'
  | 'single_choice_grid'
  | 'number_grid';

export interface FieldValidation {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  min?: number;
  max?: number;
  integerOnly?: boolean;
  customMessage?: string;
  maxFileSize?: number;
}

export type HeadingLevel = 2 | 3 | 4 | 5;
export type HeadingAlignment = 'left' | 'center' | 'right';
export type HeadingFontStyle = 'normal' | 'italic';

export type HeadingFontFamily =
  | 'Inter'
  | 'Arial'
  | 'Times New Roman'
  | 'Georgia'
  | 'Verdana'
  | 'Courier New'
  | 'Comic Sans MS';

export type SpacerSize = 'sm' | 'md' | 'lg' | 'xl';

export type HeadingFontWeight = 200 | 400 | 500 | 600 | 700;

export type ParagraphLevel = 2 | 3 | 4 | 5;

export type ParagraphAlignment = 'left' | 'center' | 'right';

export type ParagraphFontStyle = 'normal' | 'italic';

export type ParagraphFontFamily =
  | 'Inter'
  | 'Arial'
  | 'Times New Roman'
  | 'Georgia'
  | 'Verdana'
  | 'Courier New'
  | 'Comic Sans MS';

export type ParagraphFontWeight = 200 | 400 | 500 | 600 | 700;

export type DividerStyle = 'solid' | 'dashed' | 'dotted';

export type DividerThickness = 1 | 2 | 3 | 4;

export type DividerOpacity = 25 | 50 | 75 | 100;

export type TextareaResize = 'none' | 'vertical' | 'horizontal' | 'both';

export type TextareaRows = 3 | 5 | 8 | 10;

export type DateFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';

/** Static literal or data-library variable binding. */
export type BoundValue =
  | { source: 'static'; value: string | number | boolean }
  | {
      source: 'variable';
      dataLibraryId: string;
      label?: string;
      key?: string;
      /** Resolved display value from API hydrate or client enrichment. */
      value?: string | number | boolean | null;
    }
  | {
      source: 'system';
      systemKey: string;
      label?: string;
      key?: string;
      /** Resolved display value from API hydrate. */
      value?: string | number | boolean | null;
    };

/** Live reference to a data-library collection as the field's option source. */
export interface FormFieldDataLibraryCollectionRef {
  dataLibraryId: string;
  /** Column key used as the primary display label. */
  displayFieldKey?: string;
  /** Client-only primary column name. */
  displayFieldLabel?: string;
  /** Column key used as secondary supporting text (searchable picker). */
  secondaryDisplayFieldKey?: string;
  /** Client-only secondary column name. */
  secondaryDisplayFieldLabel?: string;
  /** Field keys used for searchable picker search. */
  searchFields?: string[];
  /** Page size for searchable picker pagination. */
  pageSize?: number;
  /** Client-only display name. */
  label?: string;
  /** Client-only collection key. */
  key?: string;
}

export interface CheckboxConfig {
  defaultValue?: boolean;
}

export interface NumberConfig {
  defaultValue?: number;
}

export interface SwitchConfig {
  defaultValue?: boolean;
}

export interface TextConfig {
  /** @deprecated Prefer FormField.default */
  defaultValue?: string;
}

export interface RangeSliderConfig {
  defaultValue?: number;
}

export interface RatingConfig {
  max?: 5 | 10;
  defaultValue?: number;
}

export interface CurrencyConfig {
  /**
   * Locked default for respondents. When null/omitted, the control shows USD
   * initially but respondents can change currency.
   */
  defaultCurrency?: string | null;
  defaultValue?: number;
  allowChangeCurrency?: boolean;
}

export interface CheckboxField {
  id: string;
  type: 'checkbox';
  config: CheckboxConfig;
  validation?: FieldValidation;
}

export interface TextField {
  id: string;
  type: 'text';
  config: TextConfig;
  validation?: FieldValidation;
}

export interface NumberField {
  id: string;
  type: 'number';
  config: NumberConfig;
  validation?: FieldValidation;
}

export interface SwitchField {
  id: string;
  type: 'switch';
  config: SwitchConfig;
  validation?: FieldValidation;
}

export interface RangeSliderField {
  id: string;
  type: 'range_slider';
  config: RangeSliderConfig;
  validation?: FieldValidation;
}

export interface SingleChoiceConfig {
  options?: string[];
  defaultValue?: string;
}

export type MultiChoiceGridResponseType = 'checkbox' | 'dropdown' | 'button';

export interface MultiChoiceGridConfig {
  responseType?: MultiChoiceGridResponseType | null;
  options: GridChoiceOption[];
  items: string[];
  allowAdditionalRows?: boolean;
  addRowButtonLabel?: string;
  itemsDataLibraryCollection?: FormFieldDataLibraryCollectionRef;
  defaultValue?: string;
}

export interface MultiChoiceConfig {
  options?: string[];
  defaultValue?: string[];
}

export type SingleChoiceGridResponseType = 'button' | 'radio' | 'dropdown';

export interface SingleChoiceGridConfig {
  responseType: SingleChoiceGridResponseType;
  items: string[];
  choice?: GridChoiceOption[];
  options?: GridChoiceOption[];
  allowAdditionalRows: boolean;
  addRowButtonLabel?: string;
  defaultValue?: string;
  itemsDataLibraryCollection?: FormFieldDataLibraryCollectionRef;
}

export function readSingleChoiceGridConfig(field: FormField): SingleChoiceGridConfig {
  const config = field.config as
    | (Partial<Omit<SingleChoiceGridConfig, 'choice' | 'options'>> & {
        choice?: unknown;
        options?: unknown;
      })
    | undefined;

  const choice = hydrateGridChoices(config?.choice ?? config?.options ?? []);

  return {
    responseType: config?.responseType ?? 'button',
    items: config?.items ?? [],
    choice,
    options: choice,
    allowAdditionalRows: config?.allowAdditionalRows ?? false,
    addRowButtonLabel: config?.addRowButtonLabel ?? '+ Add another item',
    itemsDataLibraryCollection: config?.itemsDataLibraryCollection,
  };
}

/** Reads multi-choice defaults from in-memory `string[]` or a persisted JSON string. */
export function parseMultiChoiceDefault(value: unknown): string[] {
  if (Array.isArray(value)) {
    return uniqueMultiChoiceDefaults(value);
  }

  if (typeof value !== 'string') {
    return [];
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return [];
  }

  if (trimmed.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);

      if (Array.isArray(parsed)) {
        return uniqueMultiChoiceDefaults(parsed);
      }
    } catch {
      return [trimmed];
    }
  }

  return [trimmed];
}

/** Persists multi-choice defaults through the scalar bound-value DTO. */
export function serializeMultiChoiceDefault(values: readonly string[]): string | undefined {
  const next = uniqueMultiChoiceDefaults(values);
  return next.length > 0 ? JSON.stringify(next) : undefined;
}

export function readMultiChoiceDefaults(field: FormField): string[] {
  const configValue =
    field.config && 'defaultValue' in field.config ? field.config.defaultValue : undefined;

  if (Array.isArray(configValue)) {
    return parseMultiChoiceDefault(configValue);
  }

  if (field.default?.source === 'static') {
    return parseMultiChoiceDefault(field.default.value);
  }

  return parseMultiChoiceDefault(configValue);
}

function uniqueMultiChoiceDefaults(values: readonly unknown[]): string[] {
  const seen = new Set<string>();
  const next: string[] = [];

  for (const value of values) {
    const text = typeof value === 'string' ? value.trim() : '';

    if (!text || seen.has(text)) {
      continue;
    }

    seen.add(text);
    next.push(text);
  }

  return next;
}

export function readMultiChoiceGridConfig(field: FormField): MultiChoiceGridConfig {
  const config = field.config as
    | (Partial<Omit<MultiChoiceGridConfig, 'options'>> & { options?: unknown })
    | undefined;

  return {
    responseType: config?.responseType ?? 'checkbox',
    items: config?.items ?? [],
    options: hydrateGridChoices(config?.options ?? []),
    allowAdditionalRows: config?.allowAdditionalRows ?? false,
    addRowButtonLabel: config?.addRowButtonLabel ?? '+ Add another item',
    itemsDataLibraryCollection: config?.itemsDataLibraryCollection,
  };
}

export interface NumberGridItem {
  id?: string;
  uniqueKey?: string;
  display: string;
  placeholder?: string;
  defaultValue?: number | null;
}

export interface NumberGridConfig {
  columns?: NumberGridColumn[];
  items?: NumberGridItem[];
  itemsDataLibraryCollection?: FormFieldDataLibraryCollectionRef;
  defaultValue?: string;
}

export function readNumberGridConfig(field: FormField): NumberGridConfig {
  const config = (field.config ?? {}) as NumberGridConfig;
  const legacyTopLevel =
    field.type === 'number_grid' ? field.dataLibraryCollection : undefined;
  return {
    columns: config.columns,
    items: config.items,
    itemsDataLibraryCollection:
      config.itemsDataLibraryCollection ?? legacyTopLevel,
    defaultValue: config.defaultValue,
  };
}

export type FieldConfig =
  | CheckboxConfig
  | NumberConfig
  | SwitchConfig
  | TextConfig
  | RangeSliderConfig
  | RatingConfig
  | CurrencyConfig
  | SingleChoiceConfig
  | MultiChoiceConfig
  | MultiChoiceGridConfig
  | SingleChoiceGridConfig
  | NumberGridConfig;

export interface FormField {
  id: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  required?: boolean;

  /** When true, field is shown but cannot be changed (readonly behavior). */
  disabled?: boolean;

  options?: string[];
  choice?: string[];

  /**
   * Live reference to a data-library collection as option source.
   * Use at most one of `options` or `dataLibraryCollection`.
   */
  dataLibraryCollection?: FormFieldDataLibraryCollectionRef;

  helpText?: string;
  validation?: FieldValidation;
  headingLevel?: HeadingLevel;
  headingAlignment?: HeadingAlignment;
  headingFontStyle?: HeadingFontStyle;
  headingFontFamily?: HeadingFontFamily;
  headingColor?: string;
  spacerSize?: SpacerSize;
  headingFontWeight?: HeadingFontWeight;
  paragraphLevel?: ParagraphLevel;
  paragraphAlignment?: ParagraphAlignment;
  paragraphFontStyle?: ParagraphFontStyle;
  paragraphFontFamily?: ParagraphFontFamily;
  paragraphColor?: string;
  paragraphFontWeight?: ParagraphFontWeight;
  dividerStyle?: DividerStyle;
  dividerThickness?: DividerThickness;
  dividerColor?: string;
  dividerOpacity?: DividerOpacity;
  textareaRows?: TextareaRows;
  textareaResize?: TextareaResize;
  textareaCharacterLimit?: number;
  textareaShowCharacterCounter?: boolean;
  checkboxLinkText?: string;
  checkboxLinkUrl?: string;
  dateMin?: Date | null;
  dateMax?: Date | null;
  dateDisablePast?: boolean;
  dateDisableFuture?: boolean;
  dateDefault?: Date | null;
  dateFormat?: DateFormat;
  orientation?: 'vertical' | 'horizontal';
  radioSize?: 'sm' | 'md' | 'lg';
  sliderMin?: number;
  sliderMax?: number;
  sliderStep?: number;
  sliderUnit?: string;
  maxFileSize?: number;
  maxImages?: number;

  /** Static or variable default value. */
  default?: BoundValue;

  config?: FieldConfig;

  // Date field

  // Date Range field
  startDate?: Date | null;
  endDate?: Date | null;
  minDate?: Date | null;
  maxDate?: Date | null;

  dateRangeAllowOnlyPast?: boolean;
  dateRangeAllowOnlyFuture?: boolean;
  dateRangeAllowToday?: boolean;

  dateRangeDisabledDays?: Weekday[];
  dateRangeDisabledDates?: Date[];

  dateRangeShowCalendarIcon?: boolean;
  dateRangeShowClearButton?: boolean;

  dateRangeScrollBehavior?: 'block' | 'reposition';
  dateRangeMobilePresentation?: 'modal' | 'bottomSheet' | 'fullScreen';

  sliderDefaultStart?: number;
  sliderDefaultEnd?: number;

  hint?: string;
  mapDefaultLocation?: MapLocationValue | null;
}

/** Matches NGL SignalTextField `appearance` values. */
export type FormInputAppearance =
  | 'fill'
  | 'outline'
  | 'ghost'
  | 'floating'
  | 'ifta'
  | 'floatingOutline';

export type ThemeShade = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;

/** Full Tailwind palette (50–950) stored when a theme swatch is selected. */
export type FormThemeShades = Record<ThemeShade, string>;

export interface FormPageCta {
  primaryLabel: string;
  /** Omit on first page (order === 1) so Back is not shown. */
  backLabel?: string;
}

export interface FormPage {
  id: string;
  /** 1-based display/navigation order. */
  order: number;
  title?: string;
  description?: string;
  cta: FormPageCta;
  fields: FormField[];
}

export interface BuiltForm {
  title: string;
  description?: string;
  /** Optional cover image shown at the top of the form preview. */
  coverImageUrl?: string;
  /** Visual style for form inputs. Default `fill`. */
  inputAppearance?: FormInputAppearance;
  /** Full Tailwind shade map (50–950). Default slate. */
  themeColor?: FormThemeShades;
  pages: FormPage[];
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}
